/**
 * Registrar lo que vuelve firmado del trámite documentario.
 *
 * El oficio y los memorandos salen del sistema sin número: se numeran y se
 * firman en el trámite documentario. Este diálogo recibe lo que vuelve —el
 * número que asignó el trámite, su expediente si lo tiene y el PDF firmado— y
 * desde ahí ese número es el del documento.
 */
import { useEffect, useId, useState } from 'react'
import { Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, Input } from '@mpfn/ui'
import { FileUploadField, FormField } from '@mpfn/patterns'
import { avisarError } from '../lib/avisos.js'

export interface Firmado {
  numero: string
  expediente?: string
  archivo: File
}

interface PropsDialogoFirmado {
  abierto: boolean
  onCambio: (abierto: boolean) => void
  titulo: string
  descripcion: string
  /** Un número de ejemplo, con el formato del trámite. */
  ejemploNumero: string
  /** Obligatorio y nombra la acción: nada de «Aceptar» (contrato del DS). */
  etiquetaConfirmar: string
  cargando?: boolean
  onConfirmar: (firmado: Firmado) => void
}

export function DialogoFirmado({
  abierto,
  onCambio,
  titulo,
  descripcion,
  ejemploNumero,
  etiquetaConfirmar,
  cargando = false,
  onConfirmar,
}: PropsDialogoFirmado) {
  const idNumero = useId()
  const idExpediente = useId()
  const idArchivo = useId()
  const [numero, setNumero] = useState('')
  const [expediente, setExpediente] = useState('')
  const [archivo, setArchivo] = useState<File | null>(null)
  const [tocado, setTocado] = useState(false)

  // Al cerrar se limpia: reabrirlo para otro documento no debe arrastrar el
  // número del anterior.
  useEffect(() => {
    if (!abierto) {
      setNumero('')
      setExpediente('')
      setArchivo(null)
      setTocado(false)
    }
  }, [abierto])

  const numeroValido = /\d/.test(numero) && numero.trim().length >= 3
  const errorNumero = tocado && !numeroValido ? 'Escriba el número que asignó el trámite documentario.' : null
  const errorArchivo = tocado && !archivo ? 'Adjunte el PDF firmado.' : null

  return (
    <Dialog open={abierto} onOpenChange={onCambio}>
      <DialogContent>
        <DialogTitle>{titulo}</DialogTitle>
        <DialogDescription>{descripcion}</DialogDescription>

        <FormField
          id={idNumero}
          label="N.° del trámite documentario"
          required
          hint={`Como figura en el documento firmado, por ejemplo ${ejemploNumero}.`}
          {...(errorNumero ? { error: errorNumero } : {})}
        >
          <Input
            value={numero}
            onChange={(evento) => {
              setNumero(evento.target.value)
            }}
          />
        </FormField>

        <FormField id={idExpediente} label="Expediente del trámite" hint="Opcional.">
          <Input
            value={expediente}
            onChange={(evento) => {
              setExpediente(evento.target.value)
            }}
          />
        </FormField>

        <FormField id={idArchivo} label="PDF firmado" required {...(errorArchivo ? { error: errorArchivo } : {})}>
          <FileUploadField
            id={idArchivo}
            value={archivo}
            onChange={setArchivo}
            accept=".pdf"
            acceptLabel="PDF"
            maxSizeMb={20}
            variant="compact"
            onError={(mensaje) => {
              avisarError(new Error(mensaje), 'El archivo no es válido')
            }}
          />
        </FormField>

        <div className="sigfisc-dialogo__acciones">
          <DialogClose asChild>
            <Button variant="secondary" type="button">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            type="button"
            loading={cargando}
            onClick={() => {
              setTocado(true)
              if (!numeroValido || !archivo) return
              onConfirmar({
                numero: numero.trim(),
                ...(expediente.trim() ? { expediente: expediente.trim() } : {}),
                archivo,
              })
            }}
          >
            {etiquetaConfirmar}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
